import useAppToast from 'hooks/toast/useToast'
import { createDefaultSampleProducts } from 'services/product/productServices'
import { setupShop } from 'services/shop/shopServices'
import { uploadToCdn } from 'services/ai/services'
import { useMutation } from 'react-query'
import useAppStore from 'stores/app/appStore'
import useOnboardingStore from '../stores/useOnboardingStore'
import { validateStoreData } from '../utils/shopSetupFormValidation'
import useLocaleResources from 'hooks/useLocaleResources/useLocaleResources'

export function useShopSetupSubmit() {
    const { updateState, user, shop } = useAppStore()
    const { showToast } = useAppToast()
    const { shopData, shopSetupUI, setError, resetOnboarding, updateOnboardingState } = useOnboardingStore()
    const { t } = useLocaleResources('onboarding')

    const { mutateAsync: submitShopSetup, isLoading } = useMutation({
        mutationFn: async () => {
            let finalShopData = { ...shopData }

            // Helper function to check if URL needs CDN upload
            const needsCdnUpload = (url: string) => {
                // If already uploaded to our CDN, no need to upload again
                if (url.startsWith('https://upload-file-droplinked.s3.amazonaws.com/')) {
                    return false
                }

                // Upload everything else (AI-generated images, blob URLs, data URLs, external URLs)
                return true
            }

            // Upload logo if it's AI-generated or external URL
            if (shopData.logo && needsCdnUpload(shopData.logo)) {
                try {
                    const { data } = await uploadToCdn(shopData.logo)
                    finalShopData.logo = data.cdnUrl
                }
                catch (error) {
                    console.error('Error uploading logo:', error)
                    showToast({ type: "error", message: t('useAiGeneratedContent.errors.failedToUploadLogo') })
                }
            }

            // Upload banner if it's AI-generated or external URL
            if (shopData.hero_section && needsCdnUpload(shopData.hero_section)) {
                try {
                    const { data } = await uploadToCdn(shopData.hero_section)
                    finalShopData.hero_section = data.cdnUrl
                }
                catch (error) {
                    showToast({ type: "error", message: t('useAiGeneratedContent.errors.failedToUploadBanner') })
                }
            }

            const shopResponse = await setupShop(finalShopData)
            if (shopSetupUI.autoAddSampleProductsEnabled) {
                await createDefaultSampleProducts(finalShopData.logo)
            }
            return shopResponse
        },
        onSuccess: (data) => {
            updateState({ key: "shop", params: { ...shop, ...data.data.data } })
            updateState({ key: "user", params: { ...user, status: "SHOP_INFO_COMPLETED" } })
            updateOnboardingState('currentStep', 'PAYMENT_DETAILS')
        },
        onError: (error: any) => {
            const errorMessage = error?.response?.data?.data?.message
            showToast({
                type: "error",
                message: errorMessage || t('useShopSetupSubmit.errors.failedToCompleteShopSetup')
            })
        }
    })

    const handleSubmit = async () => {
        if (validateStoreData(shopData, setError, t)) {
            await submitShopSetup()
        }
    }

    return {
        handleSubmit,
        isLoading,
        resetOnboarding
    }
}